import type { WireLook } from "odyssey-shared";
import { Away, CloseCode, EntityKind, clearHeldLooks, heldLook, newHeldLooks, voiceOfAppearance } from "odyssey-shared";
import type { Footsteps } from "./audio/footsteps.ts";
import type { CharacterFigure } from "./character.ts";
import type { GameConnection } from "./connection.ts";
import type { SignInEnd } from "./disconnect.ts";
import { DoorPrompt } from "./door-prompt.ts";
import { pageKeys } from "./controls/page.ts";
import { pageKeyHints } from "./touch/key-hints.ts";
import type { Fight } from "./fight/fight.ts";
import { PAGE_HITS } from "./fight/page-hits.ts";
import { PAGE_VFX_FEEDS } from "./vfx/feed.ts";
import { VfxPlayer } from "./vfx/player.ts";
import { VFX_REGISTRY } from "./vfx/registry.ts";
import { remoteDrink, remoteLevelUp } from "./vfx/triggers.ts";
import { StanceStore } from "./fight/stance.ts";
import { PageGround } from "./ground/page-ground.ts";
import { LevelTracker } from "./hud/level-up.ts";
import { invalidateTarget } from "./interact-target.ts";
import { JOIN_TIMEOUT_MS, Join, RELEASE_BOUND_MS, type JoinAwait } from "./join.ts";
import { LeaveDesk, type LeaveAnswer } from "./leave-desk.ts";
import { closeFailure, disconnectFailure, signInFailure, silenceFailure } from "./loading-failure.ts";
import type { LoadingFailure } from "./loading-state.ts";
import { LocalPlayer } from "./local-player.ts";
import { FrozenGate } from "./moderation/frozen-gate.ts";
import { PageTargets } from "./page-targets.ts";
import { RemoteCharacters } from "./remote-characters.ts";
import { gameReport } from "./report.ts";
import { surfaceAt } from "./ground-surfaces.ts";
import { characterSource } from "./session-characters.ts";
import { bindSends } from "./session-bindings.ts";
import { bindFight, createFight } from "./session-fight.ts";
import { frameHandlers } from "./session-frames.ts";
import { SessionCrossing } from "./session-crossing.ts";
import { SessionHandoff } from "./session-handoff.ts";
import { SessionReconnect } from "./session-reconnect.ts";
import { LinkWatch, pageLinkHost } from "./link-watch.ts";
import { createGround, createTargets } from "./session-targets.ts";
import { answered, newSessionState, type FreshToken, type SessionCore, type SessionHost, type SessionLifetime, type SessionOptions, type SessionParts, type SessionState } from "./session-types.ts";
import { reloadPageForWorld } from "./world-version.ts";
import { HEAD_ABOVE_FEET, type CharacterSource } from "./world-ui/characters.ts";

export type { SessionHost, SessionOptions, SessionWorld } from "./session-types.ts";

const CONTENT_VERSION = 3;

/**
 * The join's session (P1.4-T5, P1.9-T15): the connection to the gateway and everything one welcome starts, from
 * the welcome to the close. It holds the local player and the other players' characters, tells the loading shell each
 * step of the join, reveals the world, and forgets the session when it ends. The server's frames are routed by
 * `session-frames.ts` (the welcome by `session-welcome.ts`, the snapshots by `session-snapshot.ts`), the fight and the
 * targets are made and bound by `session-fight.ts` and `session-targets.ts`, and the stores' sends by
 * `session-bindings.ts`; all read the state this class keeps. A failure before the reveal is the shell's notice: a
 * socket that closed (a refused token, a refusal, or a server that never answered) or a server that sent no welcome
 * within `JOIN_TIMEOUT_MS` (`join.ts`). A close after the reveal takes the player out of the world: the session is
 * forgotten and the shell comes back with the disconnect notice, except for a close a retry can cure (`reconnects`), after
 * which the player stays in the world, held, under a connection mark while the session tries again a few times in the
 * background (`reconnect.ts`, P1.9-T32); only when those attempts are spent does the notice show, and nothing joins
 * again by itself after it.
 */
export class GameSession {
  private readonly join: Join;
  private readonly state: SessionState = newSessionState();
  private readonly levels = new LevelTracker();
  private readonly vfx: VfxPlayer;

  private readonly reconnect: SessionReconnect;
  private readonly handoff: SessionHandoff;
  private readonly makeRemotes: () => RemoteCharacters;

  /**
   * The page's side of seamless travel (ADR-147): the other maps' figures and the crossing's welcome.
   */
  public readonly crossing: SessionCrossing;
  private readonly link: LinkWatch;

  /**
   * The look each player in the view holds, by handle, as its name is held (ADR-094 decision 6, `held-looks.ts`):
   * a `looks` frame's looks wait for the snapshot of their tick, and are dropped on despawn, on a change of map and
   * on reconnect, which are all a session's end here.
   */
  private readonly looks = newHeldLooks();

  /**
   * The local player's character.
   */
  public readonly local = new LocalPlayer();

  /**
   * The prompt of a door entered with E, which the interaction asks when E is pressed.
   */
  public readonly door: DoorPrompt;

  /**
   * The other players' characters.
   */
  public readonly remotes: RemoteCharacters;

  /**
   * The fight on the page: the swing, the combat frame's events, the player's own death and respawn, and its effects.
   */
  private readonly fight: Fight;
  private readonly steps: Footsteps | null;

  /**
   * The player's target (P2.2-T3): a click's, Tab's or Escape's selection, sent as `target`, named in every act and
   * shown in the HUD's target frame.
   */
  public readonly targets: PageTargets;

  /**
   * The weapon drawn or sheathed (P2.2-T13, `fight/stance.ts`): the page's intent alone, which a left click reads.
   */
  public readonly stance: StanceStore;

  /**
   * The page's refusal of an act while a Game Master has frozen its player (ADR-056's amendment of 2026-10-06,
   * decision 42): the fight, the target, the PvP switch, an item, a loot take and E each ask it before anything
   * starts, and it says one line at most once in 2 seconds. With no moderation on the page it refuses nothing.
   */
  public readonly gate: FrozenGate;

  /**
   * The page's ground items (P2.8-T65): its own items' marks, their drawing and the pick-up E and a click ask.
   */
  public readonly ground: PageGround;

  /**
   * The characters the scene draws, as world-attached UI reads them (`world-ui/characters.ts`): the local character
   * where its sprite stands this frame, and every player character and creature drawn, the local one first, with the
   * state the client's view holds for it and how far its art reaches above its feet, a hop's lift included, so the plate
   * rises with the head (ADR-116's third amendment); the player's target; and the world's look table. The nameplates
   * read it; the scene names no surface.
   */
  public readonly characters: CharacterSource;

  /**
   * The page's question to the gateway before a chosen leave, and its leave. A leave the gateway takes departs the
   * world at once.
   */
  private readonly leaveDesk = new LeaveDesk({ send: (confirm) => this.connection?.sendLeave(confirm) ?? false, left: () => void this.depart() });

  /**
   * A page makes one session, and the stores it is handed (the bar's, the bag's, the chest's) live exactly as long as
   * it does, so what it binds and subscribes to on them here is never ended; a second session over the same stores
   * would hear each of their events twice and is not supported.
   *
   * @param options What the page hands the session.
   * @param host What the session asks of the scene.
   */
  public constructor(
    private readonly options: SessionOptions,
    private readonly host: SessionHost,
  ) {
    gameReport.projectiles.roundTripMs = () => this.connection?.perf.smoothedRttMs ?? null;
    const sounds = options.sounds;
    this.vfx = new VfxPlayer(VFX_REGISTRY, PAGE_VFX_FEEDS, sounds === undefined ? null : (hook, own, dx, dy) => sounds.hookPlayed(hook, own, dx, dy));
    const fightSounds = sounds?.fight((handle) => this.connection?.view.entities.get(handle)?.kind ?? -1);
    this.local.onChainBlow = fightSounds === undefined ? null : (ability) => fightSounds.used(ability);
    this.stance = options.stance ?? new StanceStore();
    this.gate = new FrozenGate({ frozen: () => options.moderation?.frozen() ?? false, held: () => this.connection?.view.entities.get(this.state.handle)?.away === Away.Held, say: (text) => host.messageLine.say(text), now: () => performance.now() });
    const core: SessionCore = { options, host, state: this.state, connection: () => this.connection, local: this.local, gate: this.gate };
    this.ground = createGround(core, fightSounds);
    const steps = sounds?.footsteps((x, y) => surfaceAt(host.world()?.surfaces ?? null, x, y)) ?? null;
    this.steps = steps;
    this.makeRemotes = () =>
      new RemoteCharacters(
        (state) => (state.kind === EntityKind.Creature ? host.creature(state.appearance, state.handle) : state.kind === EntityKind.Npc ? host.npc(state.appearance) : this.dress(host.character(), state.handle)),
        (state) => (state.kind === EntityKind.Creature ? host.creatureHead(state.appearance) : state.kind === EntityKind.Npc ? host.npcHead(state.appearance) : (host.playerHead?.() ?? HEAD_ABOVE_FEET)),
        sounds === undefined ? null : (kind, appearance, sound, dx, dy) => (kind === EntityKind.Player ? sounds.action(kind, undefined, sound, dx, dy, voiceOfAppearance(appearance)) : sounds.action(kind, kind === EntityKind.Creature ? host.world()?.looks[appearance] : undefined, sound, dx, dy)),
        undefined,
        steps === null ? null : (cadence, x, y, walking, time, dx, dy, running) => steps.otherFrame(cadence, x, y, walking, time, dx, dy, running),
        remoteLevelUp(this.vfx),
        remoteDrink(this.vfx),
        sounds === undefined ? null : (handle, appearance, time, dx, dy) => sounds.npcDrawn(handle, host.world()?.npcLooks[appearance]?.sound ?? null, time, dx, dy),
      );
    this.remotes = this.makeRemotes();
    this.targets = createTargets(core, this.remotes, (out) => this.characters.local(out));
    this.fight = createFight(core, this.targets, this.stance, fightSounds, this.vfx);
    const parts: SessionParts = { ...core, remotes: this.remotes, fight: this.fight, targets: this.targets, ground: this.ground, looks: this.looks, levels: this.levels, vfx: this.vfx, stance: this.stance };
    this.crossing = new SessionCrossing(parts, this.makeRemotes);
    this.link = new LinkWatch(pageLinkHost(options.hud));
    this.reconnect = new SessionReconnect({
      state: this.state,
      hud: options.hud,
      account: options.account,
      timing: options.reconnectTiming,
      freshToken: () => options.freshToken?.() ?? "ready",
      drop: () => this.join.drop(),
      open: () => this.join.open(),
      started: () => options.progress({ type: "started", step: "welcome", total: 1 }),
      openWith: (fresh) => this.openWith(fresh),
      leave: (failure) => this.leave(failure),
    });
    const lifetime: SessionLifetime = { roundTrip: (rttMs) => this.link.sample(rttMs), giveUp: (failure) => this.giveUp(failure), silent: (awaited) => this.silent(awaited), reset: () => this.reset(false), crossed: () => this.forgetMap(), stalled: (failure) => this.leave(failure), close: (code, reason) => this.onClose(code, reason), leaveAnswered: (answer) => this.leaveDesk.answered(answer) };
    this.door = new DoorPrompt(options.hud, { keys: pageKeys, hints: pageKeyHints });
    this.handoff = new SessionHandoff(parts, lifetime);
    this.join = new Join(
      { url: options.url, token: options.token, contentVersion: CONTENT_VERSION, latencyMs: options.latencyMs, deadlineMs: JOIN_TIMEOUT_MS, frozen: this.gate.frozen },
      frameHandlers(parts, lifetime, this.handoff, this.door, this.crossing),
    );
    this.characters = characterSource(parts, this.crossing);
    bindSends(parts);
    bindFight(parts, fightSounds);
  }

  /**
   * Tells a figure just made for a player the look its handle holds, if one has arrived: a look comes before the
   * snapshot that spawns its character, and the figure is made when the character is first drawn.
   *
   * @param figure The figure.
   * @param handle The player's handle.
   * @returns The figure.
   */
  private dress(figure: CharacterFigure, handle: number): CharacterFigure {
    const look = heldLook(this.looks, handle);
    if (look !== undefined) {
      this.host.wear?.(figure, look);
    }
    return figure;
  }

  /**
   * The look the page's own character holds, as it last arrived.
   *
   * @returns The look, or undefined outside a session and before the own look has arrived.
   */
  public ownLook(): Readonly<WireLook> | undefined {
    return heldLook(this.looks, this.state.handle);
  }

  /**
   * The connection to the gateway, while there is one.
   *
   * @returns The current connection, or null before the first and once one is given up.
   */
  public get connection(): GameConnection | null {
    return this.join.connection;
  }

  /**
   * Sounds the page's own character's footsteps for the frame just drawn (ADR-085's amendment for P1.9-T84).
   *
   * @param time Scene time in ms.
   */
  public ownStep(time: number): void {
    this.steps?.ownFrame(this.local.rendered.x, this.local.rendered.y, this.local.walking, time, this.local.running);
  }

  /**
   * Tells the sound where the page's own character is rendered in the frame just drawn, which every sound placed
   * in the world is heard from while it plays (ADR-085's amendment for P1.12-T10).
   */
  public listen(): void {
    this.options.sounds?.listenerAt(this.local.rendered.x, this.local.rendered.y);
  }

  /**
   * The local player's handle.
   *
   * @returns The handle the welcome gave, or -1 outside a session.
   */
  public get handle(): number {
    return this.state.handle;
  }

  /**
   * Whether the session has its welcome.
   *
   * @returns True from the welcome until the session is forgotten.
   */
  public get welcomed(): boolean {
    return this.state.welcomed;
  }

  /**
   * Whether the session has applied a snapshot since its welcome.
   *
   * @returns True from the first snapshot until the session is forgotten.
   */
  public get snapshotSeen(): boolean {
    return this.state.snapshotSeen;
  }

  /**
   * Whether the world is shown.
   *
   * @returns True from the reveal until the page leaves the world.
   */
  public get revealed(): boolean {
    return this.state.revealed;
  }

  /**
   * Whether the loading shell's Retry may act: never once the world is shown, where a close offers its own way
   * back, nor once the sign-in has ended.
   *
   * @returns True when a retry may run.
   */
  public get retryable(): boolean {
    return !this.state.revealed && !this.state.signInEnded;
  }

  /**
   * Whether the world is held while the session tries again in the background (P1.9-T32): from the close until the
   * new connection's first snapshot. The scene sends nothing and moves nothing meanwhile.
   *
   * @returns True while held.
   */
  public get holding(): boolean {
    return this.reconnect.holding || this.handoff.waiting;
  }

  /**
   * Whether the character is between maps or the world is held: a handoff to another map is under way or arriving, or the
   * session is trying again in the background. The touch controls hide for as long as it holds.
   *
   * @returns True while in transfer.
   */
  public get inTransfer(): boolean {
    return this.state.handoff !== null || this.holding;
  }

  /**
   * Whether the page is arriving on another map: the new map's welcome is applied and the world is not yet drawn with the
   * character on it (ADR-122 decision 2.9).
   *
   * @returns True from the welcome after a handoff until `arrived`.
   */
  public get arriving(): boolean {
    return this.state.handoff?.landed === true;
  }

  /**
   * The new map is drawn with the character on it: the world fades back in.
   */
  public arrived(): void {
    this.handoff.arrived();
  }

  /**
   * Called after each drawn frame: ends a background reconnect whose connection has sent its first snapshot.
   */
  public mend(): void {
    this.reconnect.mend();
  }

  /**
   * Shows a failure on the loading shell; a world failure's detail is also the report's `error`.
   *
   * @param failure The failure.
   */
  public fail(failure: LoadingFailure): void {
    if (failure.category === "world") {
      gameReport.error = failure.detail;
    }
    this.options.progress({ type: "failed", failure });
  }

  /**
   * Opens a connection to the gateway presenting the page's newest token (`join.ts`), which gives the server
   * `JOIN_TIMEOUT_MS` to answer it and keeps any earlier connection's frames from the session. A page whose sign-in
   * has ended opens none, since its notice already asks the player to log in or reload, and a world load still
   * running when it ended must not join behind that notice.
   */
  public connect(): void {
    if (this.state.signInEnded) {
      return;
    }
    this.options.progress({ type: "started", step: "welcome", total: 1 });
    const ready = this.options.freshToken?.() ?? "ready";
    if (ready === "ready") {
      this.join.open();
      return;
    }
    void ready.then((fresh) => this.openWith(fresh));
  }

  /**
   * Acts on what the refresh made before a join came to (`SessionOptions.freshToken`): joins with the token it left, ends
   * the sign-in when it ended, and, when the gateway did not answer, gives up with the
   * unreachable server's failure rather than present a lapsed token, which `hello` would answer by ending the sign-in.
   *
   * @param fresh What the refresh came to.
   */
  private openWith(fresh: FreshToken): void {
    if (this.state.signInEnded) {
      return;
    }
    if (fresh === "expired" || fresh === "naming") {
      this.endSignIn(fresh);
    } else if (fresh === "failed") {
      this.giveUp(closeFailure(1006, "", this.options.account));
    } else {
      this.join.open();
    }
  }

  /**
   * A left click on the world, at a point in world pixels: selects the character, creature or dummy whose feet box
   * holds it, then, while the weapon is drawn, swings toward it, once the world is shown (`fight/fight.ts`, question
   * 4), the act naming the selection; sheathed, the selection is all it does (P2.2-T13).
   *
   * @param x The point's x in world pixels.
   * @param y The point's y in world pixels.
   * @returns True when a swing started and its act was sent.
   */
  public swing(x: number, y: number): boolean {
    if (this.state.revealed) {
      this.targets.selectAt(x, y, this.host.now());
    }
    return this.fight.swing(x, y, this.host.now());
  }

  /**
   * A tap on the world that only selects, as a finger on a touch page does: the character, creature or dummy whose feet
   * box holds the point, once the world is shown, and nothing else.
   *
   * @param x The point's x in world pixels.
   * @param y The point's y in world pixels.
   */
  public select(x: number, y: number): void {
    if (this.state.revealed) {
      this.targets.selectAt(x, y, this.host.now());
    }
  }

  /**
   * The touch Attack button, once the world is shown: the weapon's basic blow at the selected target, or along the
   * facing (`fight/fight.ts`).
   *
   * @returns True when an act was sent and the swing started.
   */
  public attack(): boolean {
    return this.state.revealed && this.fight.attack(this.host.now());
  }

  /**
   * R (P2.2-T13): sheathes or draws the weapon, once the world is shown.
   *
   * @returns True when it toggled.
   */
  public toggleStance(): boolean {
    return this.stance.toggle();
  }

  /**
   * A slot's key (P2.2-T4, question 4): uses the slot toward a point in world pixels, the pointer's, once the world is
   * shown, as a left click uses slot 1 (`fight/fight.ts`). The item slot's key asks the server to use the item it
   * shows instead (ADR-091 decision 14), which the bag sends only once the world is shown. Every key acts on its slot
   * whichever page of a paged bar shows; one whose slot is not shown flashes the swap control when its act or request
   * was sent (`bar/pages.ts`), so a press that sends nothing flashes nothing.
   *
   * @param slot The slot's index, from 0.
   * @param x The point's x in world pixels.
   * @param y The point's y in world pixels.
   * @returns True when its act was sent.
   */
  public useSlot(slot: number, x: number, y: number): boolean {
    const used = this.options.bar?.holdsItem(slot) === true ? this.options.bar.use(slot) : this.state.revealed && this.fight.use(slot, x, y, this.host.now());
    if (used) {
      this.options.bar?.pages.keyed(slot);
    }
    return used;
  }

  /**
   * Fires a click held for its swing's cooldown the moment the swing is ready, toward the world point under the
   * pointer now (`fight/fight.ts`); the scene calls it each frame right after the frame's inputs are sent.
   *
   * @param x The pointer's world x in world pixels.
   * @param y The pointer's world y in world pixels.
   * @returns True when the held click fired.
   */
  public fireHeld(x: number, y: number): boolean {
    return this.fight.update(x, y, this.host.now());
  }

  /**
   * Reveals the world: the loading shell finishes its last step and steps aside, and the report calls the world
   * ready.
   */
  public reveal(): void {
    this.state.revealed = true;
    this.options.progress({ type: "finished", step: "view" });
    this.options.progress({ type: "revealed" });
    gameReport.ready = true;
  }

  /**
   * Gives up the current connection before the reveal, forgets any session it started, and shows the failure.
   *
   * @param failure Why.
   */
  private giveUp(failure: LoadingFailure): void {
    this.handoff.cancel();
    if (this.reconnect.failure(failure)) {
      return;
    }
    this.join.drop();
    this.reset();
    this.link.stop();
    this.fail(failure);
  }

  /**
   * The connection closed. Before the reveal, the close is the loading shell's failure alone: a refused token
   * (4001), which also tells the page so an account session is forgotten, a refusal, or a server that never
   * answered; the message line is left empty, so nothing of the refusal outlives a Retry that reaches the world, and
   * the failure offers Reload in place of Retry where the close's notice reloads the page (`disconnect.ts`: a stale
   * page, a message it cannot read, a code it does not know). A world version the server has moved past (4007)
   * reloads the page, which loads the version the server now runs (`world-version.ts`), and is a notice with Reload
   * only when the minute's count of world reloads is spent.
   * After the reveal a close takes the player out of the world to the disconnect notice for its code (P1.9-T15), and
   * the message line never shows it: the notice says why in a player's words, its action is the way back, and its
   * detail line keeps the code and reason for support. A close by a GM (4008) shows what the gateway told just before
   * it, the kick's or the ban's reason (ADR-056's amendment of 2026-10-04), and the page's moderation forgets what the
   * connection held.
   *
   * @param code The close code.
   * @param reason The server's reason.
   */
  private onClose(code: number, reason: string): void {
    this.handoff.cancel();
    gameReport.closed = `${code} ${reason}`;
    this.leaveDesk.ended();
    this.options.chat?.receive({ kind: "ended" });
    this.options.emotes?.ended();
    const told = code === CloseCode.Moderated ? (this.options.moderation?.closed() ?? null) : null;
    this.options.moderation?.ended();
    if (code === CloseCode.WorldUpdated && reloadPageForWorld(null, "closed")) {
      return;
    }
    if (code === CloseCode.AuthFailed) {
      this.options.onAuthFailed?.();
    }
    if (code === CloseCode.Maintenance) {
      this.options.onMaintenanceStart?.(answered(this.state));
    }
    if (this.state.revealed) {
      if (this.reconnect.close(code, reason)) {
        return;
      }
      this.leave(disconnectFailure(code, reason, this.options.account, told));
      return;
    }
    this.giveUp(closeFailure(code, reason, this.options.account, told));
  }

  /**
   * A connection said nothing in time: a failed attempt inside a background reconnect, else a join that failed.
   *
   * @param awaited What the server did not send.
   */
  private silent(awaited: JoinAwait): void {
    if (!this.reconnect.silent()) {
      this.giveUp(silenceFailure(JOIN_TIMEOUT_MS, awaited));
    }
  }

  /**
   * Takes the page out of the world it showed, or out of the join it was making (P1.9-T15): the connection is given
   * up, the session it started is forgotten, the message line is cleared, the report no longer calls the world
   * ready, and the loading shell comes back with the notice. Nothing joins again until the player acts on the
   * notice.
   *
   * @param failure The notice.
   */
  private leave(failure: LoadingFailure): void {
    this.handoff.cancel();
    this.reconnect.stop();
    this.link.stop();
    this.options.reading.close();
    this.options.dialogue?.close();
    this.host.messageLine.clear();
    this.join.drop();
    this.reset();
    this.state.revealed = false;
    gameReport.ready = false;
    this.options.progress({ type: "left", failure });
  }

  /**
   * Departs the world for character select (P1.9-T62, ADR-065's note "Change character and the release of the
   * character"): as a close would, the reading panel and the message line clear, the session is forgotten and the
   * report no longer calls the world ready, and the loading shell takes the reveal back with no notice; the
   * connection is released, and the promise waits for the gateway to answer its close, which takes the character
   * out of the world and writes it behind (ADR-013), or for `RELEASE_BOUND_MS`. Nothing joins again until the page
   * asks (`GameScene.retry`).
   *
   * @returns A promise that settles once the character is released or the bound has passed; it never rejects.
   */
  public depart(): Promise<void> {
    this.handoff.cancel();
    this.reconnect.stop();
    this.link.stop();
    this.options.reading.close();
    this.options.dialogue?.close();
    this.host.messageLine.clear();
    const released = this.join.release(RELEASE_BOUND_MS);
    this.reset();
    this.state.revealed = false;
    gameReport.ready = false;
    this.options.progress({ type: "departed" });
    return released;
  }

  /**
   * The game menu's Change character or Log out asks the gateway (ADR-084's amendment of 2026-10-13, `LeaveDesk`):
   * what a leave now would meet, or, confirmed, to leave. When the gateway takes a confirmed leave the page departs
   * in the turn its answer arrives (`depart`), so the close that follows finds a connection already given up and
   * shows no notice; the character stays in the world for the answer's `stay` ticks.
   *
   * @param confirm False to ask and change nothing, true to leave.
   * @returns The answer, or null when there was none: no connection, a connection not yet welcomed or already ended,
   *   or a gateway that did not answer in time. It never rejects.
   */
  public askLeave(confirm: boolean): Promise<LeaveAnswer | null> {
    return this.leaveDesk.ask(confirm);
  }

  /**
   * Tells the gateway the player leaves the login queue (P1.4-T164, ADR-127 decision 5), which ends the place it
   * holds for the account. The page then departs for character select (`depart`), or, with no select to go to,
   * waits for the gateway's close.
   *
   * @returns Whether the gateway was told: false with no connection, once it has welcomed the page, or once the
   *   connection has ended.
   */
  public leaveQueue(): boolean {
    return this.connection?.sendQueueLeave() ?? false;
  }

  /**
   * The page's sign-in ended while it played (P1.9-T15): its account session could no longer be renewed, or the
   * gateway answered that the account needs a name first. The page leaves the world as a close would, so the
   * character is saved as it leaves and no longer stands in the world behind the notice, and never joins again; the
   * notice's Log in or Reload takes the player on.
   *
   * @param end Why the sign-in ended.
   */
  public endSignIn(end: SignInEnd): void {
    this.state.signInEnded = true;
    this.leave(signInFailure(end));
  }

  /**
   * Forgets what belonged to the map a seamless crossing leaves (ADR-147 decision 18, note 14), as `reset` does for a
   * door but keeping everything that carries over: the local character, its prediction, sprite and seq, the figures (which
   * the crossing hands over), the bag, the bars, the belt, the wallet, the character window, the companion, the run, the
   * levels, the death screen, chat and emotes. What it forgets names handles of the map left (the target, the fight's
   * plan and kill tally, the held looks, the hit numbers and effects over characters) or lies on its ground (things,
   * shots, ground items, harvests, chests, doors, the reading and the conversation, the world map's places and the party's
   * marks, which the map reached sends again).
   */
  private forgetMap(): void {
    this.options.reading.close();
    this.options.dialogue?.close();
    this.options.sounds?.npcsGone();
    this.steps?.reset();
    this.fight.crossed();
    this.targets.reset();
    this.options.travel?.reset();
    this.door.reset();
    this.options.harvest?.reset();
    this.options.worldMap?.reset();
    this.options.partyPositions?.reset();
    this.options.chest?.reset();
    PAGE_HITS.clear();
    PAGE_VFX_FEEDS.character.clear();
    clearHeldLooks(this.looks);
    gameReport.players = [];
    const world = this.host.world();
    if (world !== null) {
      invalidateTarget(world.lookup);
      world.things.clear();
      world.projectiles.clear();
    }
    this.ground.clear();
  }

  /**
   * Forgets a session that ended, before the reveal or once the page has left the world it showed (P1.9-T15), so
   * the next welcome starts clean: the characters it drew are destroyed, its things cleared and their footprints
   * lifted from the grid, its shots emptied, and the predictor, the interpolator and the handle dropped. A reset for a
   * welcome or a handoff to another map keeps the socket, so the chat and the emote wheel the gateway opened on it once
   * at the login stay open: the gateway never sends `blocks` or `emotes` again on that socket.
   *
   * @param connectionEnded Whether the connection ends with the session, which ends the chat and the emote wheel too.
   */
  private reset(connectionEnded = true): void {
    if (connectionEnded) {
      this.options.chat?.receive({ kind: "ended" });
      this.options.emotes?.ended();
    }
    this.options.reading.close();
    this.options.dialogue?.close();
    this.local.end();
    this.crossing.clear();
    this.remotes.clear();
    this.options.sounds?.npcsGone();
    this.steps?.reset();
    if (this.options.sounds !== undefined) {
      this.options.sounds.ownVoice = null;
    }
    this.fight.reset();
    this.targets.reset();
    this.options.run?.reset();
    this.options.bar?.reset();
    this.options.bag?.reset();
    this.options.wallet?.reset();
    this.options.character?.resetSession();
    this.options.travel?.reset();
    this.door.reset();
    this.options.harvest?.reset();
    this.options.worldMap?.reset();
    this.options.partyPositions?.reset();
    this.options.arranged?.forget();
    this.options.belt?.forget();
    this.options.companion?.reset();
    this.options.chest?.reset();
    this.options.loot?.clear();
    PAGE_HITS.clear();
    PAGE_VFX_FEEDS.character.clear();
    PAGE_VFX_FEEDS.xpBar.clear();
    clearHeldLooks(this.looks);
    this.state.handle = -1;
    this.state.welcomed = false;
    this.state.queued = false;
    this.state.snapshotSeen = false;
    this.levels.reset();
    this.options.levelUp?.clear();
    this.options.discoveries?.clear();
    this.options.death?.reset();
    this.state.vitalsSeen = false;
    gameReport.welcomed = false;
    gameReport.players = [];
    const world = this.host.world();
    if (world !== null) {
      invalidateTarget(world.lookup);
      world.things.clear();
      world.projectiles.clear();
    }
    this.ground.clear();
  }
}
